if (!customElements.get('tr-date-picker')) {
  class TrDatePicker extends HTMLElement {
    selectors = {
      alternateRadio: '[data-tr-js-gift-card-alternate-radio]',
      alternateOption: '[data-tr-js-gift-card-alternate-option]',
      immediateRadio: '[data-tr-js-gift-card-immediate-radio]',
      input: '[data-tr-js-date-picker-input]',
      calendar: '[data-tr-js-date-picker-calendar]',
      month: '[data-tr-js-date-picker-month]',
      days: '[data-tr-js-date-picker-days]',
      previousMonth: '[data-tr-js-date-picker-previous-month]',
      nextMonth: '[data-tr-js-date-picker-next-month]',
      error: '[data-tr-js-date-picker-error]',
      alternateSummary: '[data-tr-js-gift-card-alternate-summary]',
      timezoneOffset: '[data-tr-js-gift-card-timezone-offset]',
      form: 'form'
    };

    attributes = {
      errorMessage: 'data-tr-js-date-picker-error-message'
    };

    stateClasses = {
      isOpen: 'is-open'
    };

    connectedCallback() {
      this.alternateRadioElement = this.closest(this.selectors.form)?.querySelector(this.selectors.alternateRadio);
      this.alternateOptionElement = this.closest(this.selectors.form)?.querySelector(this.selectors.alternateOption);
      this.immediateRadioElement = this.closest(this.selectors.form)?.querySelector(this.selectors.immediateRadio);
      this.inputElement = this.querySelector(this.selectors.input);
      this.calendarElement = this.querySelector(this.selectors.calendar);
      this.monthElement = this.querySelector(this.selectors.month);
      this.daysElement = this.querySelector(this.selectors.days);
      this.previousMonthElement = this.querySelector(this.selectors.previousMonth);
      this.nextMonthElement = this.querySelector(this.selectors.nextMonth);
      this.errorElement = this.querySelector(this.selectors.error);
      this.alternateSummaryElement = this.closest(this.selectors.form)?.querySelector(this.selectors.alternateSummary);
      this.timezoneOffsetElement = this.closest(this.selectors.form)?.querySelector(this.selectors.timezoneOffset);
      this.formElement = this.closest(this.selectors.form);

      this.minimumDate = this.getStartOfDay(new Date());
      this.maximumDate = new Date(this.minimumDate);
      this.maximumDate.setDate(this.maximumDate.getDate() + 90);
      this.currentMonth = new Date(this.minimumDate.getFullYear(), this.minimumDate.getMonth(), 1);
      this.selectedDate = null;
      this.wasAlternateOptionSelected = this.alternateRadioElement?.checked;

      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      if (this.timezoneOffsetElement) {
        this.timezoneOffsetElement.value = new Date().getTimezoneOffset().toString();
      }

      this.renderCalendar();
    }

    bindEvents() {
      this.alternateRadioElement?.addEventListener('change', this.onNotificationChange);
      this.alternateRadioElement?.addEventListener('click', this.onAlternateRadioClick);
      this.immediateRadioElement?.addEventListener('change', this.onNotificationChange);
      this.previousMonthElement?.addEventListener('click', this.onPreviousMonthClick);
      this.nextMonthElement?.addEventListener('click', this.onNextMonthClick);
      this.daysElement?.addEventListener('click', this.onDayClick);
      this.formElement?.addEventListener('submit', this.onFormSubmit);
    }

    onNotificationChange = () => {
      if (this.alternateRadioElement?.checked) {
        this.wasAlternateOptionSelected = true;
        this.openCalendar();
        return;
      }

      this.wasAlternateOptionSelected = false;
      this.selectedDate = null;
      this.inputElement.value = '';
      this.inputElement.disabled = true;
      this.updateAlternateSummary();
      this.closeCalendar();
      this.clearError();
    };

    onAlternateRadioClick = () => {
      if (!this.wasAlternateOptionSelected) {
        this.openCalendar();
        return;
      }

      if (this.classList.contains(this.stateClasses.isOpen)) {
        this.closeCalendar();
      } else {
        this.openCalendar();
      }
    };

    onPreviousMonthClick = () => {
      this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
      this.renderCalendar();
    };

    onNextMonthClick = () => {
      this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
      this.renderCalendar();
    };

    onDayClick = (event) => {
      const dayButton = event.target.closest('[data-tr-js-date-picker-day]');

      if (!dayButton || dayButton.disabled) return;

      this.selectedDate = this.dateFromIso(dayButton.dataset.trJsDatePickerDay);
      this.inputElement.value = this.toIsoDate(this.selectedDate);
      this.inputElement.disabled = false;
      this.updateAlternateSummary();
      this.closeCalendar();
      this.clearError();
      this.renderCalendar();
    };

    onFormSubmit = (event) => {
      if (!this.alternateRadioElement?.checked || this.selectedDate) return;

      event.preventDefault();
      this.openCalendar();
      this.errorElement.textContent = this.getAttribute(this.attributes.errorMessage);
      this.errorElement.hidden = false;
    };

    renderCalendar() {
      const monthFormatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        month: 'long',
        year: 'numeric'
      });
      const monthStart = new Date(this.currentMonth);
      const monthEnd = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
      const dayButtons = [];

      this.monthElement.textContent = monthFormatter.format(this.currentMonth);
      this.previousMonthElement.disabled = monthStart <= new Date(this.minimumDate.getFullYear(), this.minimumDate.getMonth(), 1);
      this.nextMonthElement.disabled = monthEnd >= this.maximumDate;

      for (let emptyDay = 0; emptyDay < monthStart.getDay(); emptyDay += 1) {
        dayButtons.push('<span class="tr-date-picker__day-placeholder" aria-hidden="true"></span>');
      }

      for (let day = 1; day <= monthEnd.getDate(); day += 1) {
        const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), day);
        const isUnavailable = date < this.minimumDate || date > this.maximumDate;
        const isSelected = this.selectedDate && this.toIsoDate(date) === this.toIsoDate(this.selectedDate);
        const dateLabel = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }).format(date);

        dayButtons.push(
          `<button class="tr-date-picker__day${isSelected ? ' is-selected' : ''}" type="button" role="gridcell" aria-label="${dateLabel}" aria-pressed="${Boolean(isSelected)}" data-tr-js-date-picker-day="${this.toIsoDate(date)}"${isUnavailable ? ' disabled' : ''}>${day}</button>`
        );
      }

      this.daysElement.innerHTML = dayButtons.join('');
    }

    openCalendar() {
      this.calendarElement.hidden = false;
      this.classList.add(this.stateClasses.isOpen);
    }

    closeCalendar() {
      this.calendarElement.hidden = true;
      this.classList.remove(this.stateClasses.isOpen);
    }

    updateAlternateSummary() {
      if (!this.alternateSummaryElement) return;

      if (!this.selectedDate) {
        this.alternateSummaryElement.textContent = this.alternateSummaryElement.dataset.trJsGiftCardAlternateSummaryDefault;
        return;
      }

      this.alternateSummaryElement.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(this.selectedDate);
    }

    clearError() {
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    getStartOfDay(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    toIsoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    dateFromIso(value) {
      const [year, month, day] = value.split('-').map(Number);

      return new Date(year, month - 1, day);
    }

    destroy() {
      this.alternateRadioElement?.removeEventListener('change', this.onNotificationChange);
      this.alternateRadioElement?.removeEventListener('click', this.onAlternateRadioClick);
      this.immediateRadioElement?.removeEventListener('change', this.onNotificationChange);
      this.previousMonthElement?.removeEventListener('click', this.onPreviousMonthClick);
      this.nextMonthElement?.removeEventListener('click', this.onNextMonthClick);
      this.daysElement?.removeEventListener('click', this.onDayClick);
      this.formElement?.removeEventListener('submit', this.onFormSubmit);
    }
  }

  customElements.define('tr-date-picker', TrDatePicker);
}
